import type * as React from "react";
import { Navigate, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import {
  useCheckFeatureFlag,
  useFeatureFlagStates,
} from "@/shared/domain/featureFlags/hooks";
import { hasSessionEvents } from "@/shared/domain/orgs";
import { QueryTarget } from "@/shared/domain/query";
import { sessionOrgId } from "@/shared/domain/sessions/loaders/sessionOrgId";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { Goto, SessionTab } from "@/shared/navigation";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import { orgEventModelRoute } from "@/shared/routing/orgEventModelRoute";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { CreateSessionPage } from "./CreateSession";
import {
  EventsTab,
  SessionAgentThreads,
  SessionAISummary,
  SessionDetail,
  SessionDetailsTab,
  SessionInvocations,
  SessionMetricsTab,
  SourcesTab,
  TopicsTab,
} from "./SessionDetail";
import { useSessionOutletContext } from "./SessionDetail/useSessionOutletContext";
import { SessionsPage } from "./SessionsPage";

/**
 * Renders `tab`, the Agents or Summary tab, when `release-session-ai` is on
 * in the session's org, the same check that puts those tabs in the strip;
 * otherwise redirects to the Topics tab. Renders nothing until the flags have
 * loaded, so a page load that starts on one of these tabs is not redirected
 * before the flag is known.
 */
function SessionAITab({ tab }: { tab: React.ReactElement }) {
  const { session } = useSessionOutletContext();
  const { isPending } = useFeatureFlagStates();
  const enabled = useCheckFeatureFlag("release-session-ai", session.org_id);

  if (isPending) {
    return null;
  }
  return enabled ? (
    tab
  ) : (
    <Navigate
      to={Goto.createSessionRoute(session.session_id, SessionTab.Topics)}
      replace
    />
  );
}

/**
 * Route tree for `/sessions`.
 *
 * - The list page (`/sessions`) preloads saved table preferences.
 * - The create page (`/sessions/create`) is a sibling of the list, outside
 *   its table-preferences loader, which only the list page consumes.
 * - The detail layout (`/sessions/:sessionId/*`) is a sibling rather
 *   than a child of the list, because the detail page owns its own chrome.
 * - The `events` tab opens only in an org that has session events.
 *
 * Spread into the top-level route list (`{...SessionsRoutes()}`).
 */
export const SessionsRoutes = () => [
  <Route
    key="sessions"
    path="/sessions"
    loader={composeLoaders(
      loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      ),
      // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
      makeViewsLoader(queryClient, { target: QueryTarget.Sessions }),
    )}
    element={<SessionsPage />}
  />,
  // React Router ranks the static `create` segment above `:sessionId`, so
  // `/sessions/create` never matches the detail route regardless of
  // declaration order.
  <Route
    key="sessions-create"
    path="/sessions/create"
    element={<CreateSessionPage />}
  />,
  <Route
    key="session-detail"
    path="/sessions/:sessionId"
    element={<SessionDetail />}
  >
    <Route index element={<Navigate to={SessionTab.Topics} replace />} />
    <Route path={SessionTab.Topics} element={<TopicsTab />} />
    <Route path={SessionTab.Sources} element={<SourcesTab />} />
    {orgEventModelRoute({
      path: SessionTab.Events,
      allows: hasSessionEvents,
      redirectTo: ({ params }) =>
        Goto.createSessionRoute(params.sessionId ?? "", SessionTab.Topics),
      owningOrgId: sessionOrgId,
      element: <EventsTab />,
      // See `EventsRoutes`: the events list snapshots its saved table
      // preferences on mount, and a `?view=` link's View must be resolved
      // before the list composes its first query.
      loader: composeLoaders(
        loadSearchTablePreferences(
          queryClient,
          "event_search_table_preferences",
        ),
        makeViewsLoader(queryClient, { target: QueryTarget.Events }),
      ),
    })}
    <Route path={SessionTab.Metrics} element={<SessionMetricsTab />} />
    <Route path={SessionTab.Actions} element={<SessionInvocations />} />
    <Route
      path={SessionTab.Agents}
      element={<SessionAITab tab={<SessionAgentThreads />} />}
    />
    <Route
      path={SessionTab.Summary}
      element={<SessionAITab tab={<SessionAISummary />} />}
    />
    <Route path={SessionTab.Details} element={<SessionDetailsTab />} />
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ sessionId }) =>
            Goto.createSessionRoute(sessionId ?? "", SessionTab.Topics)
          }
        />
      }
    />
  </Route>,
];
